"use client";

/**
 * Client body for /business-value — the business case in plain language:
 * hero → four value blocks (mechanisms, no promised percentages) → scale &
 * avoided-cost band → ROI calculator (visitor's own assumptions) → CTA.
 * All copy lives in the `businessValuePage` namespace (EN + NL in lockstep);
 * the calculator reads plan prices from `marketing-pricing.ts`.
 */

import { ClipboardCheck, GitBranch, ScrollText, ShieldCheck, TrendingUp, Users } from "lucide-react";
import { MarketingShell } from "@/components/marketing/marketing-shell";
import {
  CtaBanner,
  FeatureGrid,
  Section,
  SectionHeader,
  UseCaseHero,
} from "@/components/marketing/marketing-kit";
import { RoiCalculator } from "./roi-calculator";
import { useT } from "@/i18n";

export function BusinessValueContent() {
  const t = useT();
  const k = (s: string) => `businessValuePage.${s}`;

  return (
    <MarketingShell>
      <UseCaseHero
        eyebrow={t(k("hero.eyebrow"))}
        title={t(k("hero.title"))}
        highlight={t(k("hero.highlight"))}
        subtitle={t(k("hero.subtitle"))}
        primary={{ href: "#calculator", label: t(k("hero.primaryCta")) }}
        secondary={{ href: "/#video", label: t(k("hero.secondaryCta")) }}
      />

      {/* ── Four value blocks ─────────────────────────────────────────────── */}
      <Section id="value">
        <SectionHeader
          align="left"
          eyebrow={t(k("blocks.eyebrow"))}
          title={t(k("blocks.title"))}
          description={t(k("blocks.description"))}
        />
        <FeatureGrid
          columns={2}
          features={[
            { icon: <Users size={18} />, title: t(k("blocks.items.labor.title")), body: t(k("blocks.items.labor.body")) },
            { icon: <GitBranch size={18} />, title: t(k("blocks.items.recovery.title")), body: t(k("blocks.items.recovery.body")) },
            { icon: <ShieldCheck size={18} />, title: t(k("blocks.items.governance.title")), body: t(k("blocks.items.governance.body")) },
            { icon: <ScrollText size={18} />, title: t(k("blocks.items.compliance.title")), body: t(k("blocks.items.compliance.body")) },
          ]}
        />
      </Section>

      {/* ── Scale without proportional headcount + avoided costs ─────────── */}
      <Section muted>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="rounded-2xl border border-line-subtle bg-bg-raised p-6 md:p-8">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-bg-elev text-brand-300 border border-line-subtle">
              <TrendingUp size={18} />
            </span>
            <h3 className="mt-5 text-[16px] font-semibold text-fg-bright">{t(k("scale.title"))}</h3>
            <p className="mt-2 text-[13.5px] leading-relaxed text-fg-dim">{t(k("scale.body"))}</p>
          </div>
          <div className="rounded-2xl border border-line-subtle bg-bg-raised p-6 md:p-8">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-bg-elev text-brand-300 border border-line-subtle">
              <ClipboardCheck size={18} />
            </span>
            <h3 className="mt-5 text-[16px] font-semibold text-fg-bright">{t(k("scale.avoidedTitle"))}</h3>
            <p className="mt-2 text-[13.5px] leading-relaxed text-fg-dim">{t(k("scale.avoided"))}</p>
          </div>
        </div>
      </Section>

      {/* ── ROI calculator ────────────────────────────────────────────────── */}
      <Section id="calculator">
        <SectionHeader
          eyebrow={t(k("calc.eyebrow"))}
          title={t(k("calc.title"))}
          description={t(k("calc.description"))}
        />
        <RoiCalculator />
        <div className="mx-auto mt-8 max-w-3xl rounded-2xl border border-line-subtle bg-bg-raised/60 p-5">
          <h3 className="text-[13.5px] font-semibold text-fg-bright">{t(k("calc.formulaTitle"))}</h3>
          <p className="mt-1.5 text-[13px] leading-relaxed text-fg-dim">{t(k("calc.formulaBody"))}</p>
        </div>
      </Section>

      <CtaBanner
        title={t(k("cta.title"))}
        description={t(k("cta.description"))}
        primary={{ href: "/signup", label: t(k("cta.primary")) }}
        secondary={{ href: "/demo", label: t(k("cta.secondary")) }}
      />
    </MarketingShell>
  );
}
